import React, { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';

import LoadingSpinner from 'components/loading-spinner';

import { AxiosResponse } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { useLanguageState } from 'contexts/language/language-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import _, { isEmpty } from 'underscore';
import { saveState } from 'utils/storage';
import { CreateFieldCollectionInput } from 'views/fish-check/types/create-field-collection-input.type';
import { FieldCollectionType } from 'views/fish-check/types/field-collection-type.enum';

import { Box, Button, useTheme } from '@mui/material';
import FlexBox from 'components/flex-box';
import Paper from 'components/paper';
import { usePagination } from 'hooks/use-pagination';
import { postFieldCollectionByType } from 'views/fish-check/api/create-field-collection';
import { deleteFieldCollection } from 'views/fish-check/api/delete-field-collection';
import { getFieldCollectionByCompany } from 'views/fish-check/api/get-field-collection';
import FishCheckTabsHeader from '../../components/fish-check-tabs-header';
import { getReportTemplates } from './api/get-templates';

type FormValues = {
  globalSiteId: string;
  request: CreateFieldCollectionInput;
};

type Props = {};

const ListWelfareScoring: React.FC<Props> = () => {
  const history = useHistory();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const { language } = useLanguageState();
  const snackbarDispatch = useSnackbarDispatch();

  const PAGINATION_LIMIT = 10;
  const PAGE_NAME_ID_LIST_WELFARE = 'fishCheck.listWelfareScoring';
  const PAGE_NAME_ID_FIELD_REGISTRATION = 'fishCheck';
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);

  // STATE
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [templateFields, setTemplateFields] = useState<{ label: string; value: string }[]>([]);
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // FORMS
  const { handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: {
      globalSiteId: '',
      request: {
        active: true,
        name: '',
      },
    },
    shouldUnregister: true,
  });

  const watchTemplateName = useWatch({
    control: control,
    name: 'request.name',
  });

  // #region FETCH DATA
  const { data: list, isLoading } = useGet(
    ['getFieldCollectionByCompany', account.company?.companyId, FieldCollectionType.Welfare],
    ({ queryKey }) => getFieldCollectionByCompany(String(queryKey[1]), queryKey[2] as FieldCollectionType),
    {
      enabled: !!account.company?.companyId,
    }
  );

  const { data: templates } = useGet(
    ['getReportTemplates', account.company?.companyId, language],
    ({ queryKey }) => getReportTemplates(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.companyId,
    }
  );

  // #endregion FETCH DATA

  // #region POST DATA
  const createFieldCollection = usePost<number, FormValues>(
    (request) => postFieldCollectionByType(request.globalSiteId, FieldCollectionType.Welfare, request.request),
    (response) => {
      saveState({ key: `welfare_fields_${response}`, state: templateFields.map((x) => x.value), storage: 'local' });
      history.push(`/fish-check/welfare-scoring/${response}`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const removeWelfareScoring = usePost<AxiosResponse, number>(
    (request) => deleteFieldCollection(request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getFieldCollectionByCompany']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region HANDLERS
  const handleCreateFieldCollection = (data: FormValues) => {
    createFieldCollection.mutate({
      globalSiteId: data.globalSiteId,
      request: data.request,
    });
  };

  const handleDeleteWelfareScoring = (fieldCollectionId: number) => {
    removeWelfareScoring.mutate(fieldCollectionId);
  };

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  // #endregion HANDLERS

  // #region RENDERS
  const renderRows = () => {
    const rows = _.chain(list)
      .filter((item) => (isEmpty(selectedSite) ? true : selectedSite === item.globalSiteId))
      .sortBy((p) => p.dateCreated)
      .reverse()
      .value();
    return rows;
  };

  // #endregion RENDERS

  // #region HOOKS
  useEffect(() => {
    !!templates &&
      setTemplateFields([
        ...(templates
          .find((x) => x.name === watchTemplateName)
          ?.fields.map((field) => {
            return { label: field.name, value: field.value };
          }) || []),
      ]);
  }, [watchTemplateName, templates]);

  // #endregion HOOKS
  return (
    <>
      <FishCheckTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              selectedSite
                ? [
                    {
                      text: account.company?.addressAccess.find((s) => s.id === selectedSite)?.name || selectedSite,
                      url: ``,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      <Paper transparent>
        <FilterSidebar>
          <Select
            label={intl.formatMessage({ id: `${PAGE_NAME_ID_LIST_WELFARE}.list.siteNameLabel` })}
            value={selectedSite}
            onChange={(event) => setSelectedSite(String(event.target.value))}>
            {_.chain(
              account.company?.addressAccess
                .filter((s) => !!s.seaCloudId)
                .map((s) => ({
                  siteName: s.name,
                  globalSiteId: s.id,
                }))
            )
              .uniq(false, (s) => s.globalSiteId)
              .sortBy((s) => s.siteName.toLowerCase())
              .value()
              .map((site) => (
                <RadioSelectItem key={site.globalSiteId} value={site.globalSiteId}>
                  {site.siteName}
                </RadioSelectItem>
              ))}
          </Select>
          <Box>
            <Dialog
              title={intl.formatMessage({ id: `${PAGE_NAME_ID_FIELD_REGISTRATION}.navigation.dialog.templateAndSiteLabel` })}
              button={
                <Button fullWidth sx={{ mb: { xs: 3, md: 0 } }}>
                  <FormattedMessage id={`${PAGE_NAME_ID_FIELD_REGISTRATION}.navigation.dialog.title`} />
                </Button>
              }>
              <form onSubmit={handleSubmit(handleCreateFieldCollection)}>
                <FlexBox>
                  {createFieldCollection.isPending && <LoadingSpinner overlay />}
                  <Select
                    dark
                    fullWidth
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID_FIELD_REGISTRATION}.navigation.dialog.siteLabel` })}
                    controller={{
                      name: 'globalSiteId',
                      control: control,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}>
                    {account.company?.addressAccess
                      .filter((site) => !!site.seaCloudId)
                      .filter((site) => site.id)
                      .map((site) => (
                        <RadioSelectItem key={site.id} value={site.id} selected={site.id === watch('globalSiteId')}>
                          {site.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                  <Select
                    dark
                    fullWidth
                    controller={{
                      name: 'request.name',
                      control: control,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID_FIELD_REGISTRATION}.navigation.dialog.templateLabel` })}>
                    {templates?.map((template) => (
                      <RadioSelectItem key={template.id} value={template.name} selected={template.name === watchTemplateName}>
                        {template.name}
                      </RadioSelectItem>
                    ))}
                  </Select>

                  <Box display={'flex'} justifyContent={'end'}>
                    <Button type="submit">
                      <FormattedMessage id={`general.start`} values={{ type: '' }} />
                    </Button>
                  </Box>
                </FlexBox>
              </form>
            </Dialog>
          </Box>
        </FilterSidebar>

        <List
          options={[
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID_LIST_WELFARE}.list.nameLabel` }) },
            { id: 'siteName', label: intl.formatMessage({ id: `${PAGE_NAME_ID_LIST_WELFARE}.list.siteNameLabel` }) },
            { id: 'dateCreated', label: intl.formatMessage({ id: `${PAGE_NAME_ID_LIST_WELFARE}.list.dateCreatedLabel` }) },
            { id: 'actions', label: '', align: 'center', autoWidth: true },
          ]}
          isLoading={isLoading}
          noResult={{
            show: renderRows().length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID_LIST_WELFARE}.list.noResults`} />,
          }}>
          {renderRows()
            .slice(from, to)
            .map((row, idx) => (
              <ListRow key={row.id} onClick={() => handleOnClick(`/fish-check/welfare-scoring/${row.id}`)}>
                <ListCell>
                  <span style={{ whiteSpace: 'break-spaces' }}>{row.name}</span>
                </ListCell>
                <ListCell align="left">{row.siteName}</ListCell>
                <ListCell align="left">
                  <FormattedDate
                    value={row.dateCreated}
                    day="2-digit"
                    month="short"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    hour12={false}
                  />
                </ListCell>
                <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Box
                    display="flex"
                    justifyContent={{ xs: 'space-between', md: 'flex-end' }}
                    alignItems="center"
                    width={'100%'}
                    gap={4}>
                    <Dialog
                      maxWidth="sm"
                      ref={(el) => {
                        if (el) return (removeDialogRef.current[idx] = el);
                      }}
                      title={`Delete (${row.name})`}
                      button={
                        <Box>
                          <Button size="small">
                            <FormattedMessage id="general.delete" />
                          </Button>
                        </Box>
                      }>
                      <Box display={'flex'} flexDirection={'column'} gap={4}>
                        <Box>
                          <FormattedMessage id="general.deleteComment" />
                        </Box>
                        <Box display={'flex'} justifyContent={'end'} gap={4}>
                          <Button variant="outlined" type="submit" size="small" onClick={() => removeDialogRef.current[idx]?.close()}>
                            <FormattedMessage id="general.cancel" />
                          </Button>
                          <Button onClick={() => handleDeleteWelfareScoring(row.id)}>
                            <FormattedMessage id="general.yesDelete" />
                          </Button>
                        </Box>
                      </Box>
                    </Dialog>
                    <Button size="small" onClick={() => handleOnClick(`/fish-check/welfare-scoring/${row.id}`)}>
                      <FormattedMessage id="general.edit" values={{ type: '' }} />
                    </Button>
                  </Box>
                </ListCell>
              </ListRow>
            ))}
        </List>
        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default ListWelfareScoring;
